import ApplicationController from 'javascript/controllers/application_controller';
import { Rails } from 'javascript/utils/rails_polyfills';
import { SEATING_PLAN_SEATS_PREFIX } from 'javascript/utils/ticket_cart_storage';

export default class extends ApplicationController {
  static targets = [
    'svg',
    'popup',
    'popupTitle',
    'popupDetails',
    'popupTemplate',
    'soldOutPopup',
    'notification',
  ];

  connect() {
    this.showingPopup = false;
    this.selectedSeats = new Map();
    this.originalStyles = new Map();

    document.addEventListener('remove-seat', this.handleSeatRemove);

    document.addEventListener('seating-area-click', this.handleAreaClick);

    // Add our critical CSS for seat highlighting
    this.addSeatStyles();

    super.connect();

    this.looseTickets = document.querySelectorAll(
      '.tickets-container > .ticket-selection--loose-ticket .ticket',
    );

    if (this.svgTarget.dataset.url) {
      const seatingPlanElement = this.svgTarget.closest(
        '[data-seating-plan-config]',
      );

      Promise.all([
        this.fetchSvg(),
        this.fetchConfig(seatingPlanElement),
        this.fetchTicketTypes(seatingPlanElement),
      ]).then(() => {
        this.afterConnect(seatingPlanElement);
        Rails.fire(this.svgTarget, 'howler:imageLoad');
      });
    } else {
      this.afterConnect();
    }
  }

  disconnect() {
    // Clean up anything else
    this.cleanupSeatingPlan();
  }

  private;

  async fetchSvg() {
    return fetch(this.svgTarget.dataset.url)
      .then((response) => response.text())
      .then((svg) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(svg, 'image/svg+xml');
        const svgElement = doc.documentElement;

        this.svgTarget.replaceChildren(svgElement);

        const scripts = svgElement.querySelectorAll('script');

        scripts.forEach((oldScript) => {
          const newScript = document.createElement('script');

          Array.from(oldScript.attributes).forEach((attribute) => {
            newScript.setAttribute(attribute.name, attribute.value);
          });

          newScript.textContent = oldScript.textContent;

          oldScript.replaceWith(newScript);
        });
      })
      .catch((error) => console.error('Error loading SVG:', error));
  }

  fetchConfig(seatingPlanElement) {
    return fetch(seatingPlanElement.dataset.seatingPlanConfig)
      .then((response) => response.text())
      .then((config) => {
        this.seatingConfig = JSON.parse(config);
      })
      .catch((error) => console.error('Error loading seating config:', error));
  }

  fetchTicketTypes(seatingPlanElement) {
    return fetch(seatingPlanElement.dataset.ticketTypes)
      .then((response) => response.text())
      .then((config) => {
        this.ticketTypes = JSON.parse(config);
      })
      .catch((error) => console.error('Error loading ticket types:', error));
  }

  afterConnect(seatingPlanElement) {
    this.currency = seatingPlanElement
      ? seatingPlanElement.dataset.currency
      : 'EUR';
    this.seatingPlanId = seatingPlanElement
      ? seatingPlanElement.dataset.seatingPlanId
      : null;

    // Find the ticket selection controller
    this.ticketSelectionController =
      this.application.getControllerForElementAndIdentifier(
        document.querySelector('[data-controller~="ticket-selection"]'),
        'ticket-selection',
      );

    // Initialize variables
    this.activeSeat = null;
    this.selectedSeats = new Map();

    // Debug specific table element
    const tableElement = this.svgTarget.querySelector('[data-id="table-40"]');

    // Make all SVG style classes unique using the seating plan ID
    this.makeStylesUnique();

    // Create the filter for image elements
    this.createSvgFilters();

    // Add grid container class to wrapper
    const wrapper = this.svgTarget.closest('.seating-plan-wrapper');
    if (!wrapper.classList.contains('seating-plan-wrapper')) {
      wrapper.classList.add('seating-plan-wrapper');
    }

    // Restore selectedSeats from sessionStorage before wiring up seat
    // styling/handlers, so seats already in the cart come back highlighted
    // and clicking them again is treated as a removal, not a fresh add.
    this.restoreSelectedSeats();

    this.setupClickHandlers();
    this.createSvgFilters();

    // Bind document click handler for proper cleanup later
    // this.boundDocumentClickHandler = this.handleDocumentClick.bind(this);
    // document.addEventListener('click', this.boundDocumentClickHandler);

    const scrollToCartButton = this.notificationTarget.childNodes[1];
    const cart = document.querySelector(
      '.ticket-selection-layout > .ticket-selection-layout__cart-wrapper',
    );
    scrollToCartButton.addEventListener('click', (_e) => {
      cart.scrollIntoView({ behavior: 'smooth' });
    });

    this.hideLinkedTickets(); // Add this line to hide linked tickets when controller connects
  }

  setupSvg() {
    const svg = this.svgTarget.querySelector('svg');
    if (!svg) {
      return;
    }

    // Get the form width to match
    const form = document.getElementById('ticket_order_form');
    if (!form) {
      return;
    }

    const formWidth = form.offsetWidth;

    // Get SVG's viewBox
    const viewBox = svg.getAttribute('viewBox');
    if (!viewBox) {
      return;
    }

    const [, , vbWidth, vbHeight] = viewBox.split(' ').map(Number);
    const aspectRatio = vbWidth / vbHeight;

    // Calculate height based on form width and aspect ratio
    const newHeight = formWidth / aspectRatio;

    // Set dimensions
    svg.style.width = `${formWidth}px`;
    svg.style.height = `${newHeight}px`;
  }

  showPopup() {
    this.popupTarget.classList.add('show');
  }

  // Method specifically for the close button to ensure proper event handling
  handleCloseButtonClick(event) {
    // Always prevent default to stop form submission
    event.preventDefault();
    event.stopPropagation();

    // Then call hidePopup passing the event
    this.hidePopup(event);

    // Explicitly return false to prevent form submission
    return false;
  }

  hidePopup(event) {
    // Prevent default behavior if called from an event
    if (event && event.preventDefault) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Check if we have an active seat that's not in the cart
    if (this.activeSeat && !this.selectedSeats.has(this.activeSeat.uniqueId)) {
      // Reset the color of the seat that was temporarily highlighted
      this.resetSeatColor(this.activeSeat.element);
    }

    this.popupTarget.classList.remove('show');

    // Clear the active seat reference
    this.activeSeat = null;

    // Return false to ensure any calling function can prevent form submission
    return false;
  }

  setupClickHandlers() {
    // Single delegated click listener instead of one addEventListener per
    // seat - avoids attaching ~4500 listeners on a big seating plan.
    if (!this.boundHandleSeatClick) {
      this.boundHandleSeatClick = this.handleSeatClick.bind(this);
      this.svgTarget.addEventListener('click', this.boundHandleSeatClick, true);
    }

    // Find all seats with data-seat-id
    const seats = this.svgTarget.querySelectorAll('[data-id]');

    for (const seat of seats) {
      const seatId = seat.getAttribute('data-id');

      // Validate seatId is a string and doesn't contain potentially harmful patterns
      if (
        !seatId ||
        typeof seatId !== 'string' ||
        seatId.includes('__proto__') ||
        seatId.includes('constructor') ||
        seatId.includes('prototype') ||
        seatId.includes('.') ||
        seatId.includes('[') ||
        seatId.includes(']')
      ) {
        console.error('Invalid seat ID:', seatId);
        return;
      }

      // Safely access the config property using hasOwnProperty check
      const hasConfig = Object.prototype.hasOwnProperty.call(
        this.seatingConfig,
        seatId,
      );

      // Additional validation to prevent object injection
      let config = null;
      if (
        hasConfig &&
        Object.prototype.toString.call(this.seatingConfig[seatId]) ===
          '[object Object]' &&
        Object.keys(this.seatingConfig[seatId]).length < 20
      ) {
        // Reasonable size limit - config is only read, never mutated, so no
        // need to defensively deep-clone it here
        config = this.seatingConfig[seatId];
      } else {
        config = {};
      }

      // Default to non-selectable unless explicitly configured.
      // Dimming/undimming of child shapes is handled by the
      // seat-dimmed/seat-active CSS classes (see addSeatStyles) instead of
      // touching every descendant element directly, avoids a
      // querySelectorAll('*') + per-child style write for every seat.
      seat.style.cursor = 'not-allowed';
      seat.classList.remove('clickable-seat', 'seat-active');
      seat.classList.add('seat-dimmed');

      // Find the targetable element for applying colors
      const targetElement = this.findTargetableElement(seat);

      // Only make selectable if it has a ticket type AND can_select is true
      if (config && config.ticket_type && config.can_select === true) {
        // Make seat selectable
        seat.style.cursor = 'pointer';
        seat.classList.remove('seat-dimmed');
        seat.classList.remove('unavailable-seat');
        seat.classList.add('clickable-seat', 'seat-active');

        // Apply the available color to the seat
        if (targetElement) {
          // Store original styling for later restoration
          this.storeOriginalStyling(seat, targetElement);

          // Apply the configured available color - pass seatId to check for per-seat color
          targetElement.style.fill = this.availableColor(seatId);
        }

        // Highlight seats from extant cart items
        const uniqueId = `${this.seatingPlanId}-${seatId}`;
        if (this.selectedSeats.has(uniqueId)) {
          this.highlightCartSeat(seat);
        }
      } else {
        // Apply the unavailable color to the seat
        if (targetElement) {
          // Store original styling for later restoration
          this.storeOriginalStyling(seat, targetElement);

          // Apply the configured unavailable color
          targetElement.style.fill = this.unavailableColor();
          targetElement.classList.add('unavailable-seat');
        }
      }
    }
  }

  selectedSeatsStorageKey() {
    return `${SEATING_PLAN_SEATS_PREFIX}${this.seatingPlanId}`;
  }
  // Save selected seat_ids to restore highlight on navigation. Dirty hack.
  persistSelectedSeatIds() {
    try {
      const seatIds = Array.from(this.selectedSeats.values()).map(
        (seat) => seat.seatId,
      );
      sessionStorage.setItem(
        this.selectedSeatsStorageKey(),
        JSON.stringify(seatIds),
      );
    } catch (error) {
      console.error('Error persisting selected seats:', error);
    }
  }

  loadPersistedSelectedSeatIds() {
    try {
      const raw = sessionStorage.getItem(this.selectedSeatsStorageKey());
      const seatIds = JSON.parse(raw);
      return Array.isArray(seatIds)
        ? seatIds.filter((seatId) => typeof seatId === 'string')
        : [];
    } catch (error) {
      console.error('Error loading persisted selected seats:', error);
      return [];
    }
  }

  // Rebuild selectedSeats from session storage. Does not change cart contents etc.
  restoreSelectedSeats() {
    const seatIds = this.loadPersistedSelectedSeatIds();
    if (!seatIds.length) return;

    seatIds.forEach((seatId) => {
      const seat = this.svgTarget.querySelector(
        `[data-id="${CSS.escape(seatId)}"]`,
      );
      if (!seat) return;

      const seatData = this.buildSeatData(seat, seatId);
      if (!seatData) return;

      this.showSeatInfo(seatData);

      this.selectedSeats.set(seatData.uniqueId, seatData);
    });
  }

  // Build seat element and ID into seatData for cart/popup/highlight.
  // Used to restoreSelectedSeats, just showing which seats were selected.
  buildSeatData(seat, seatId) {
    // Validate seatId is a string and doesn't contain potentially harmful patterns
    if (
      !seatId ||
      typeof seatId !== 'string' ||
      seatId.includes('__proto__') ||
      seatId.includes('constructor') ||
      seatId.includes('prototype') ||
      seatId.includes('.') ||
      seatId.includes('[') ||
      seatId.includes(']')
    ) {
      console.error('Invalid seat ID:', seatId);
      return null;
    }

    // Safely access the config property using hasOwnProperty check
    const hasConfig = Object.prototype.hasOwnProperty.call(
      this.seatingConfig,
      seatId,
    );

    // Additional validation to prevent object injection
    let config = null;
    if (
      hasConfig &&
      Object.prototype.toString.call(this.seatingConfig[seatId]) ===
        '[object Object]' &&
      Object.keys(this.seatingConfig[seatId]).length < 20
    ) {
      // Reasonable size limit - config is only read, never mutated
      config = this.seatingConfig[seatId];
    } else {
      config = {};
    }

    if (config.can_select === false) {
      return null; // Do nothing if the seat cannot be selected
    }

    // Get the ticket type reference (may be an ID or a name)
    const ticketTypeRef = config.ticket_type;

    if (
      typeof ticketTypeRef !== 'string' ||
      ticketTypeRef.includes('__proto__') ||
      ticketTypeRef.includes('constructor') ||
      ticketTypeRef.includes('prototype') ||
      ticketTypeRef.includes('.') ||
      ticketTypeRef.includes('[') ||
      ticketTypeRef.includes(']')
    ) {
      console.error('Invalid ticket type reference:', ticketTypeRef);
      return null;
    }

    // Additional validation against other potential attack vectors
    if (ticketTypeRef && ticketTypeRef.length > 100) {
      console.error('Ticket type reference is too long:', ticketTypeRef);
      return null;
    }

    // Find the actual ticket type data
    let ticketTypeData = null;
    let ticketTypeName = 'Unknown Ticket Type';

    // Case 1: If the reference is a numeric ID
    if (ticketTypeRef && ticketTypeRef.toString().match(/^\d+$/)) {
      // Find ticket by ID
      const foundTicketType = Object.entries(this.ticketTypes).find(
        ([, data]) => data.id.toString() === ticketTypeRef.toString(),
      );

      if (foundTicketType) {
        ticketTypeName = foundTicketType[0]; // The name
        ticketTypeData = foundTicketType[1]; // The data object
      }
    }
    // Case 2: If the reference is a name
    else if (
      ticketTypeRef &&
      typeof ticketTypeRef === 'string' &&
      !['__proto__', 'constructor', 'prototype'].includes(ticketTypeRef) &&
      !ticketTypeRef.includes('.') &&
      !ticketTypeRef.includes('[') &&
      !ticketTypeRef.includes(']') &&
      Object.prototype.hasOwnProperty.call(this.ticketTypes, ticketTypeRef)
    ) {
      ticketTypeName = ticketTypeRef;
      // Use Object.prototype.hasOwnProperty.call to safely access the property
      // This ensures we're only accessing direct properties, not inherited ones
      // that could lead to prototype pollution or command injection
      if (
        ticketTypeRef &&
        typeof ticketTypeRef === 'string' &&
        !ticketTypeRef.includes('__proto__') &&
        !ticketTypeRef.includes('constructor') &&
        !ticketTypeRef.includes('prototype') &&
        Object.prototype.hasOwnProperty.call(this.ticketTypes, ticketTypeRef)
      ) {
        // Only read, never mutated - no need to deep clone
        ticketTypeData = this.ticketTypes[ticketTypeRef];
      }
    }

    // Additional validation against other potential attack vectors
    if (ticketTypeData && ticketTypeData.id) {
      if (typeof ticketTypeData.id !== 'number' || ticketTypeData.id < 0) {
        console.error('Invalid ticket type ID:', ticketTypeData.id);
        return null;
      }
    }

    const configData = config || {};

    // Create a unique ID that includes the seating plan ID
    const uniqueId = `${this.seatingPlanId}-${seatId}`;

    return {
      element: seat,
      label: seat.getAttribute('data-label'),
      type: seat.getAttribute('data-type'),
      config: configData,
      ticketTypeName: ticketTypeName,
      ticketTypeCost: ticketTypeData?.cost || 0,
      ticketTypeDescription: ticketTypeData?.description || '',
      seatId: seatId,
      seatingPlanId: this.seatingPlanId,
      uniqueId: uniqueId,
    };
  }

  async handleSeatClick(event) {
    // Delegated listener (attached once on svgTarget) to find the clickable
    // seat the click actually landed on, if any
    const seat = event.target.closest('[data-id].clickable-seat');
    if (!seat) return;

    event.stopPropagation();
    const seatId = seat.getAttribute('data-id');

    try {
      const isSoldOut = await this.checkSeatAvailability(seatId);

      if (isSoldOut) return this.showSoldOutPopup();

      const seatData = this.buildSeatData(seat, seatId);
      if (!seatData) return;

      const { uniqueId } = seatData;

      // Update active seat
      if (this.activeSeat) {
        // Remove highlight from previous active seat if it's not in cart
        if (!this.selectedSeats.has(this.activeSeat.uniqueId)) {
          this.resetSeatColor(this.activeSeat.element);
        }
      }
      this.activeSeat = seatData;

      // Show popup with appropriate state - use uniqueId for checking
      const addDirectToCart =
        this.element.dataset.directAddToCartEnabled === 'true';
      if (this.selectedSeats.has(uniqueId)) {
        if (!!addDirectToCart) return this.addToCart(null, seatData);
        this.showRemoveFromCartPopup(seatData);
      } else {
        this.highlightSelectedSeat(seat);
        if (!!addDirectToCart) return this.addToCart(null, seatData);
        this.showAddToCartPopup(seatData);
      }
    } catch (e) {
      console.error(e);
    }
  }

  async checkSeatAvailability(seatId) {
    const res = await fetch(
      `/events/${this.element.dataset.eventSlug}/ticket_types/${this.seatingConfig[seatId].ticket_type}`,
    );
    const ticketType = await res.json();
    return ticketType.sold_out;
  }

  showSoldOutPopup() {
    this.soldOutPopupTarget.classList.add('show');
  }
  hideSoldOutPopup() {
    this.soldOutPopupTarget.classList.remove('show');
  }

  showAddToCartPopup(seatData) {
    // Get template content from the hidden template and sanitize it
    const rawTemplateContent = this.popupTemplateTarget.innerHTML;
    // Safely parse the template content to prevent XSS
    const templateContent = this.sanitizeHTML(rawTemplateContent);
    const template = templateContent; // Sanitized template as string
    const cost = this.formatCurrency(seatData.ticketTypeCost);

    // Pre-process the template to replace placeholders
    let processedTemplate = template
      .replace(
        '${ticketTypeName}',
        this.sanitizeHTML(seatData.ticketTypeName || 'No ticket type'),
      )
      .replace('${cost}', this.sanitizeHTML(cost))
      .replace('${section}', this.sanitizeHTML(seatData.config.section || '-'))
      .replace('${row}', this.sanitizeHTML(seatData.config.row || '-'))
      .replace(
        '${seatNumber}',
        this.sanitizeHTML(seatData.config.seat_number || '-'),
      )
      .replace('${seats}', this.sanitizeHTML(seatData.config.seats || '-'))
      .replace(
        '${disabled}',
        this.sanitizeHTML(
          seatData.config.disabled === true
            ? 'Yes'
            : seatData.config.disabled === false
              ? 'No'
              : '-',
        ),
      )
      .replace('${buttonText}', 'Add to Cart')
      .replace('${buttonClass}', 'add');

    // Create a temporary DOM element to work with the processed template
    const tempContainer = document.createElement('div');

    // Use DOMParser for safer HTML parsing instead of innerHTML
    const parser = new DOMParser();
    const parsedDoc = parser.parseFromString(processedTemplate, 'text/html');

    // Safely transfer the content - this avoids direct innerHTML assignment
    this.safelySetContent(tempContainer, parsedDoc.body);

    // Handle description - add content and show if not empty
    const description = seatData.ticketTypeDescription || '';

    // First sanitize description to prevent XSS
    const sanitizedDescription = this.sanitizeHTML(description);

    // Then check if it contains meaningful content
    const hasValidDescription =
      sanitizedDescription && sanitizedDescription.trim() !== '';

    if (hasValidDescription) {
      const descSection = tempContainer.querySelector('.description-section');
      if (descSection) {
        descSection.style.display = 'block';
        const contentElement = descSection.querySelector(
          '.description-content',
        );
        if (contentElement) {
          // Create a new document fragment with the sanitized HTML content
          const fragment = document.createDocumentFragment();
          const tempDiv = document.createElement('div');
          // Use safer method to set content
          this.safelySetContent(
            tempDiv,
            this.safelyParseHTML(sanitizedDescription),
          );

          // Move the nodes to the fragment
          while (tempDiv.firstChild) {
            fragment.appendChild(tempDiv.firstChild);
          }

          // Clear existing content
          while (contentElement.firstChild) {
            contentElement.removeChild(contentElement.firstChild);
          }

          // Append the fragment
          contentElement.appendChild(fragment);
        }
      }
    }

    // Safely set the content by cloning the nodes instead of using innerHTML
    this.safelySetContent(this.popupDetailsTarget, tempContainer);

    // After populating, update the rows to hide empty ones
    this.updatePopupRows();

    // Show the popup
    this.showPopup();
  }

  showRemoveFromCartPopup(seatData) {
    // Get template content and sanitize it to prevent XSS in the template itself
    const rawTemplateContent = this.popupTemplateTarget.innerHTML;
    // Safely parse the template content to prevent XSS
    const templateContent = this.sanitizeHTML(rawTemplateContent);
    const template = templateContent; // Sanitized template as string
    const cost = this.formatCurrency(seatData.ticketTypeCost);

    // Pre-process the template to replace placeholders
    let processedTemplate = template
      .replace(
        '${ticketTypeName}',
        this.sanitizeHTML(seatData.ticketTypeName || 'No ticket type'),
      )
      .replace('${cost}', this.sanitizeHTML(cost))
      .replace('${section}', this.sanitizeHTML(seatData.config.section || '-'))
      .replace('${row}', this.sanitizeHTML(seatData.config.row || '-'))
      .replace(
        '${seatNumber}',
        this.sanitizeHTML(seatData.config.seat_number || '-'),
      )
      .replace('${seats}', this.sanitizeHTML(seatData.config.seats || '-'))
      .replace(
        '${disabled}',
        this.sanitizeHTML(
          seatData.config.disabled === true
            ? 'Yes'
            : seatData.config.disabled === false
              ? 'No'
              : '-',
        ),
      )
      .replace('${buttonText}', 'Remove from Cart')
      .replace('${buttonClass}', 'remove');

    // Create a temporary DOM element to work with the processed template
    const tempContainer = document.createElement('div');

    // Use DOMParser for safer HTML parsing instead of innerHTML
    const parser = new DOMParser();
    const parsedDoc = parser.parseFromString(processedTemplate, 'text/html');

    // Safely transfer the content - this avoids direct innerHTML assignment
    this.safelySetContent(tempContainer, parsedDoc.body);

    // Handle description - add content and show if not empty
    const description = seatData.ticketTypeDescription || '';

    // First sanitize description to prevent XSS
    const sanitizedDescription = this.sanitizeHTML(description);

    // Then check if it contains meaningful content
    const hasValidDescription =
      sanitizedDescription && sanitizedDescription.trim() !== '';

    if (hasValidDescription) {
      const descSection = tempContainer.querySelector('.description-section');
      if (descSection) {
        descSection.style.display = 'block';
        const contentElement = descSection.querySelector(
          '.description-content',
        );
        if (contentElement) {
          // Create a new document fragment with the sanitized HTML content
          const fragment = document.createDocumentFragment();
          const tempDiv = document.createElement('div');
          // Use safer method to set content
          this.safelySetContent(
            tempDiv,
            this.safelyParseHTML(sanitizedDescription),
          );

          // Move the nodes to the fragment
          while (tempDiv.firstChild) {
            fragment.appendChild(tempDiv.firstChild);
          }

          // Clear existing content
          while (contentElement.firstChild) {
            contentElement.removeChild(contentElement.firstChild);
          }

          // Append the fragment
          contentElement.appendChild(fragment);
        }
      }
    }

    // Safely set the content by cloning the nodes instead of using innerHTML
    this.safelySetContent(this.popupDetailsTarget, tempContainer);

    // After populating, update the rows to hide empty ones
    this.updatePopupRows();

    // Show the popup
    this.showPopup();
  }

  updatePopupRows() {
    // First handle all attribute rows
    const rows = this.popupDetailsTarget.querySelectorAll(
      '.seating-plan-popup__row',
    );
    let hasVisibleRows = false;

    rows.forEach((row) => {
      const value = row.querySelector('.seating-plan-popup__value');
      if (!value || value.textContent === '-') {
        row.remove();
      } else {
        hasVisibleRows = true;
      }
    });

    // Show/hide the attributes section based on whether any rows are visible
    const attributesSection = this.popupDetailsTarget.querySelector(
      '.attributes-section',
    );
    if (attributesSection) {
      attributesSection.style.display = hasVisibleRows ? 'block' : 'none';
    }
  }

  handleSeatRemove = (e) => {
    e.preventDefault();

    const seatId = e.target.dataset.seatId;
    const seatEl = this.svgTarget.querySelector(
      `[data-id="${CSS.escape(seatId)}"]`,
    );
    const seat = this.buildSeatData(seatEl, seatId);

    this.activeSeat = seat;
    this.addToCart(null, seat);
  };

  addToCart(e, seatData = null) {
    e?.preventDefault();
    if (!this.activeSeat) return;

    const seatId = this.activeSeat.seatId;
    const uniqueId = this.activeSeat.uniqueId;
    const seat = this.activeSeat.element;
    const seatingPlanId = this.activeSeat.seatingPlanId;

    const addDirectToCart =
      this.element.dataset.directAddToCartEnabled === 'true';

    if (this.selectedSeats.has(uniqueId)) {
      // Remove from cart
      this.selectedSeats.delete(uniqueId);
      this.persistSelectedSeatIds();
      this.decrement(seatId, seatingPlanId);
      this.resetSeatColor(seat);
      this.hidePopup();

      if (seatData && addDirectToCart) this.hideSeatInfo(seatId);

      if (this.selectedSeats.size === 0) {
        this.hideNotification();
      } else {
        if (!seatData && !addDirectToCart)
          this.showNotification(
            `You have <span>${this.selectedSeats.size}</span> ${
              this.selectedSeats.size > 1 ? 'Tables' : 'Table'
            } in your cart.`,
          );
      }
    } else {
      // Add to cart
      this.selectedSeats.set(uniqueId, this.activeSeat);
      this.persistSelectedSeatIds();
      this.increment(seatId, seatingPlanId);
      this.highlightCartSeat(seat);
      this.hidePopup();

      if (seatData && addDirectToCart) return this.showSeatInfo(seatData);

      this.showNotification(
        `You have <span>${this.selectedSeats.size}</span> ${
          this.selectedSeats.size > 1 ? 'Tables' : 'Table'
        } in your cart.`,
      );
    }
  }

  showSeatInfo = (seatData) => {
    let seatInfoElement = document.querySelector(
      `.seating-plan__seat-info-popup:has(.seating-plan__seat-info[id=${seatData.seatId}])`,
    );
    if (seatInfoElement) return;

    const seatInfoHTML = this.buildSeatInfoPopup(seatData);

    seatInfoElement = document.createElement('div');
    seatInfoElement.className = 'seating-plan__seat-info-popup';
    seatInfoElement.innerHTML = seatInfoHTML;

    const isMobileBreakpoint = window.innerWidth <= 719;

    if (isMobileBreakpoint) {
      const mobileCartContainer = document.querySelector(
        '.cart-drawer .ticket-selection-layout__cart-wrapper',
      );
      mobileCartContainer.appendChild(seatInfoElement);
    } else {
      const desktopCartContainer = document.querySelector(
        '.ticket-selection-layout__main + .ticket-selection-layout__cart-wrapper .ticket-selection-layout__cart-container',
      );
      desktopCartContainer.appendChild(seatInfoElement);
    }

    requestAnimationFrame(() => {
      seatInfoElement.classList.add('show');
    });
  };

  hideSeatInfo = (seatId) => {
    const seatInfoElement = document.querySelector(
      `.seating-plan__seat-info-popup:has(.seating-plan__seat-info[id=${seatId}])`,
    );

    if (!seatInfoElement) return;

    seatInfoElement.classList.remove('show');
    setTimeout(() => seatInfoElement.remove(), 300);
  };

  buildSeatInfoPopup = (seatData) => {
    return `
      <div class="seating-plan__seat-info" id="${seatData.seatId}">
        <div class="seating-plan__seat-info-header">
          <span class="seating-plan__seat-info-heading">Selection</span>
          <a class="seating-plan__seat-info__close-button" data-seat-id='${
            seatData.seatId
          }' onclick="dispatchEvent(new CustomEvent('remove-seat', { bubbles: true }))">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#434343" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </a>
        </div>
        <div class="seating-plan-seat-info__seat-number-row">
          <div class="seating-plan-seat-info__label">Seat Number:</div>
          <div class="seating-plan-seat-info__value">${
            seatData.config.seat_number
          }</div>
        </div>
        <hr />
        <div class="seating-plan-seat-info__price-row">
          <div class="seating-plan-seat-info__label">Price:</div>
          <div class="seating-plan-seat-info__price">${this.formatCurrency(
            seatData.ticketTypeCost,
          )}</div>
        </div>
      </div>
    `;
  };

  showNotification(message) {
    const textContainer = this.notificationTarget.childNodes[0];
    textContainer.innerHTML = message;
    this.notificationTarget.classList.add('show');
  }

  hideNotification() {
    this.notificationTarget.classList.remove('show');
  }

  highlightSelectedSeat(seat) {
    // Find the targetable element - could be rect, image, path, etc.
    const targetElement = this.findTargetableElement(seat);

    if (!targetElement) {
      console.error('No targetable element found for seat:', seat);
      return;
    }

    // Store original styling if not already stored
    if (!this.originalStyles.has(seat)) {
      this.storeOriginalStyling(seat, targetElement);
    }

    // Remove any existing highlighting classes
    seat.classList.remove('seat-in-cart');
    targetElement.classList.remove('seat-in-cart');

    // Apply selected styling using class
    seat.classList.add('seat-selected');
    targetElement.classList.add('seat-selected');

    // For maximum compatibility, also apply styles directly
    this.applySelectedStyles(targetElement);
  }

  highlightCartSeat(seat) {
    // Find the targetable element - could be rect, image, path, etc.
    const targetElement = this.findTargetableElement(seat);

    if (!targetElement) {
      console.error('No targetable element found for seat:', seat);
      return;
    }

    // Store original styling if not already stored
    if (!this.originalStyles.has(seat)) {
      this.storeOriginalStyling(seat, targetElement);
    }

    // Remove any existing highlighting classes
    seat.classList.remove('seat-selected');
    targetElement.classList.remove('seat-selected');

    // Apply cart styling using class
    seat.classList.add('seat-in-cart');
    targetElement.classList.add('seat-in-cart');

    // For maximum compatibility, also apply styles directly
    this.applyCartStyles(targetElement);
  }

  resetSeatColor(seat) {
    // Find the targetable element - could be rect, image, path, etc.
    const targetElement = this.findTargetableElement(seat);

    if (!targetElement) {
      console.error('No targetable element found for seat:', seat);
      return;
    }

    // Get seat config to determine if it's selectable
    let isSelectable = true;
    // Get seat ID for config lookup and logging
    const seatId = seat.getAttribute('data-id');

    if (seatId && this.seatingConfig[seatId]) {
      const config = this.seatingConfig[seatId];
      // Check if the seat is selectable
      isSelectable =
        config &&
        config.ticket_type &&
        (config.can_select === true ||
          config.can_select === 'true' ||
          config.can_select == 1);
    }

    // Remove all highlighting classes
    seat.classList.remove('seat-selected', 'seat-in-cart');
    targetElement.classList.remove('seat-selected', 'seat-in-cart');

    const originalStyle = this.originalStyles.get(seat);

    if (originalStyle) {
      try {
        // Remove all highlighting classes from both the target element and its parent
        targetElement.classList.remove('seat-selected', 'seat-in-cart');

        // If this image is inside a g element, also clean up the parent classes
        const parentG = targetElement.parentElement;
        if (parentG && parentG.nodeName === 'g') {
          parentG.classList.remove('seat-selected', 'seat-in-cart');
          // Remove any highlight background
          this.removeHighlightEffect(parentG);
        }

        // Determine appropriate color based on selectability
        const targetColor = isSelectable
          ? this.availableColor(seatId)
          : this.unavailableColor();

        // Restore styling attributes but use dynamic color for fill
        if (originalStyle.stroke !== undefined) {
          if (originalStyle.stroke === 'none') {
            targetElement.removeAttribute('stroke');
          } else {
            targetElement.setAttribute('stroke', originalStyle.stroke);
          }
        }

        if (originalStyle.strokeWidth !== undefined) {
          if (originalStyle.strokeWidth === '0') {
            targetElement.removeAttribute('stroke-width');
          } else {
            targetElement.setAttribute(
              'stroke-width',
              originalStyle.strokeWidth,
            );
          }
        }

        // Remove filters
        targetElement.removeAttribute('filter');

        // Set opacity based on selectability
        targetElement.style.opacity = isSelectable ? '1' : '0.7';

        if (originalStyle.backgroundColor !== undefined) {
          targetElement.style.backgroundColor = originalStyle.backgroundColor;
        }

        if (originalStyle.borderRadius !== undefined) {
          targetElement.style.borderRadius = originalStyle.borderRadius;
        }

        if (originalStyle.boxShadow !== undefined) {
          targetElement.style.boxShadow = originalStyle.boxShadow;
        }

        if (originalStyle.outline !== undefined) {
          targetElement.style.outline = originalStyle.outline;
        }

        if (originalStyle.outlineOffset !== undefined) {
          targetElement.style.outlineOffset = originalStyle.outlineOffset;
        }

        // Use dynamic color instead of original fill
        // Apply to both targetElement and the parent seat element if they're different
        targetElement.style.fill = targetColor;

        // Apply fill color to parent element if it exists and is different
        if (targetElement !== seat && seat.nodeName === 'g') {
          seat.style.fill = targetColor;

          // Also need to apply to any direct children of the group that might render visible elements
          const childElements = seat.querySelectorAll('path, rect, circle');
          childElements.forEach((el) => {
            el.style.fill = targetColor;
          });
        }

        // For image elements, special handling
        if (targetElement.nodeName === 'image') {
          targetElement.style.opacity = isSelectable ? '1' : '0.5';

          // Apply highlight effect for available/unavailable seats
          if (!isSelectable) {
            this.createHighlightEffect(
              targetElement,
              this.unavailableColor(),
              'unavailable',
            );
          }
        }

        // Restore original classes if they were stored, but keep the dynamic state
        if (originalStyle.classes && Array.isArray(originalStyle.classes)) {
          // First clear all existing classes except for state-related ones
          // For SVG elements, we need to use setAttribute('class') instead of className
          // Remove all classes first to reset
          targetElement.setAttribute('class', '');

          // Add unavailable class if needed
          if (!isSelectable) {
            targetElement.classList.add('unavailable-seat');
          }

          // Then add back the original ones that aren't related to state
          originalStyle.classes.forEach((cls) => {
            if (
              !['seat-selected', 'seat-in-cart', 'unavailable-seat'].includes(
                cls,
              )
            ) {
              targetElement.classList.add(cls);
            }
          });
        }
      } catch (e) {
        console.error('Error restoring original styling:', e);
        // Fallback to simple restoration
        targetElement.removeAttribute('stroke');
        targetElement.removeAttribute('stroke-width');
        targetElement.removeAttribute('filter');
        targetElement.style.opacity = '1';
        targetElement.style.backgroundColor = 'transparent';
        targetElement.style.borderRadius = '0';
        targetElement.style.boxShadow = 'none';
        targetElement.style.outline = 'none';
        targetElement.style.outlineOffset = '0';

        // Also remove highlight if any
        const parentG = targetElement.parentElement;
        if (parentG && parentG.nodeName === 'g') {
          this.removeHighlightEffect(parentG);
        }
      }
    } else {
      // Determine appropriate color based on selectability
      const targetColor = isSelectable
        ? this.availableColor(seatId)
        : this.unavailableColor();

      // Simple restore but apply appropriate color based on seat status
      targetElement.style.fill = targetColor;
      targetElement.removeAttribute('stroke');
      targetElement.removeAttribute('stroke-width');
      targetElement.removeAttribute('filter');

      // For image elements, ensure opacity is reset but also apply visual state
      if (targetElement.nodeName === 'image') {
        targetElement.style.opacity = isSelectable ? '1' : '0.5';
      } else {
        // For other elements, set opacity based on selectability
        targetElement.style.opacity = isSelectable ? '1' : '0.7';
      }
    }
  }

  handleDocumentClick(event) {
    if (!this.element.contains(event.target)) {
      // Prevent default behavior and stop propagation to avoid Google Analytics issues
      event.preventDefault();
      event.stopPropagation();

      this.hidePopup();
      // Reset active seat color if not in cart
      if (
        this.activeSeat &&
        !this.selectedSeats.has(this.activeSeat.uniqueId)
      ) {
        this.resetSeatColor(this.activeSeat.element);
      }
      this.activeSeat = null;
    }
  }

  selectedColor() {
    // Check if we have a custom color in seatingConfig
    if (this.seatingConfig && this.seatingConfig.color_selected) {
      return this.seatingConfig.color_selected;
    }
    // Default color for SELECTED seats
    return '#FF9EB7'; // Transparent pink color
  }

  cartColor() {
    // Check if we have a custom color in seatingConfig
    if (this.seatingConfig && this.seatingConfig.color_in_basket) {
      return this.seatingConfig.color_in_basket;
    }
    // Default color for seats ADDED TO CART
    return '#9D8DF1'; // Light purple color
  }

  // Get color for available seats
  availableColor(seatId) {
    // First check if seatId is provided and has a custom available color
    if (seatId && this.seatingConfig) {
      // Try the exact seatId first
      if (
        this.seatingConfig[seatId] &&
        this.seatingConfig[seatId].available_color
      ) {
        return this.seatingConfig[seatId].available_color;
      }

      // Check for alternate formats - sometimes data-id might have different formatting
      // than what's stored in seatingConfig (e.g., with/without prefix)
      const possibleIds = [
        seatId,
        seatId.replace('seat-', ''),
        `seat-${seatId}`,
        seatId.toLowerCase(),
        seatId.toUpperCase(),
      ];

      for (const id of possibleIds) {
        if (this.seatingConfig[id] && this.seatingConfig[id].available_color) {
          return this.seatingConfig[id].available_color;
        }
      }
    }

    // Check if we have a custom color in seatingConfig
    if (this.seatingConfig && this.seatingConfig.color_available) {
      return this.seatingConfig.color_available;
    }

    // Default color for AVAILABLE seats
    return '#2196F3'; // Blue color
  }

  // Get color for unavailable seats
  unavailableColor() {
    // Check if we have a custom color in seatingConfig
    if (this.seatingConfig && this.seatingConfig.color_unavailable) {
      return this.seatingConfig.color_unavailable;
    }
    // Default color for UNAVAILABLE seats
    return '#F44336'; // Red color
  }

  createSvgFilters() {
    // Get the SVG element
    const svg = this.svgTarget.querySelector('svg');
    if (!svg) return;

    // Check if we already have a defs element
    let defs = svg.querySelector('defs');
    if (!defs) {
      // Create a defs element if it doesn't exist
      defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
      svg.insertBefore(defs, svg.firstChild);
    }

    // Create a unique ID for this seating plan's filters
    const filterId = `seating-filters-${this.seatingPlanId || 'default'}`;

    // Check if we already created these filters
    if (defs.querySelector(`#${filterId}`)) return;

    // Create a filter group element with a unique ID
    const filterGroup = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'g',
    );
    filterGroup.id = filterId;
    defs.appendChild(filterGroup);

    // Get custom colors or use defaults
    const selectedColor = this.selectedColor();
    const cartColor = this.cartColor();

    // Helper function to create a color filter
    const createColorFilter = (id, color) => {
      // Create filter element
      const filter = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'filter',
      );
      filter.id = id;
      filter.setAttribute('x', '-20%');
      filter.setAttribute('y', '-20%');
      filter.setAttribute('width', '140%');
      filter.setAttribute('height', '140%');

      // Create color flood effect
      const feFlood = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'feFlood',
      );
      feFlood.setAttribute('flood-color', color);
      feFlood.setAttribute('result', 'floodColor');

      // Create composite effect
      const feComposite = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'feComposite',
      );
      feComposite.setAttribute('in', 'floodColor');
      feComposite.setAttribute('in2', 'SourceGraphic');
      feComposite.setAttribute('operator', 'in');
      feComposite.setAttribute('result', 'coloredElement');

      // Create blend effect
      const feBlend = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'feBlend',
      );
      feBlend.setAttribute('in', 'coloredElement');
      feBlend.setAttribute('in2', 'SourceGraphic');
      feBlend.setAttribute('mode', 'multiply');

      // Add all effects to the filter
      filter.appendChild(feFlood);
      filter.appendChild(feComposite);
      filter.appendChild(feBlend);

      return filter;
    };

    // Create selected filter based on custom color
    const selectedFilter = createColorFilter(
      'seat-selected-filter',
      selectedColor,
    );

    // Create cart filter based on custom color
    const cartFilter = createColorFilter('seat-cart-filter', cartColor);

    // Add filters to filterGroup
    filterGroup.appendChild(selectedFilter);
    filterGroup.appendChild(cartFilter);
  }

  applySelectedStyles(targetElement) {
    const selectedColor = this.selectedColor();

    if (
      targetElement.nodeName === 'rect' ||
      targetElement.nodeName === 'path' ||
      targetElement.nodeName === 'circle'
    ) {
      // For shapes, set fill color
      targetElement.setAttribute('fill', selectedColor);
      targetElement.style.fill = selectedColor;
    } else if (targetElement.nodeName === 'image') {
      // For SVG images, create a visible highlight background
      this.createHighlightEffect(targetElement, selectedColor, 'selected');

      // Apply filter for visual indication
      targetElement.setAttribute('filter', 'url(#seat-selected-filter)');

      // Adjust opacity for visual feedback
      targetElement.style.opacity = '0.85';

      // Also add the class directly to the image
      targetElement.classList.add('seat-selected');

      // If this image is inside a g element, ensure parent has the class too
      const parentG = targetElement.parentElement;
      if (parentG && parentG.nodeName === 'g') {
        parentG.classList.add('seat-selected');
      }
    } else {
      // For other elements, try both fill and stroke
      targetElement.setAttribute('stroke', selectedColor);
      targetElement.setAttribute('stroke-width', '3');
    }
  }

  applyCartStyles(targetElement) {
    const cartColor = this.cartColor();

    if (
      targetElement.nodeName === 'rect' ||
      targetElement.nodeName === 'path' ||
      targetElement.nodeName === 'circle'
    ) {
      // For shapes, set fill color
      targetElement.setAttribute('fill', cartColor);
      targetElement.style.fill = cartColor;
    } else if (targetElement.nodeName === 'image') {
      // For SVG images, create a visible highlight background
      this.createHighlightEffect(targetElement, cartColor, 'cart');

      // Apply filter for visual indication
      targetElement.setAttribute('filter', 'url(#seat-cart-filter)');

      // Adjust opacity for visual feedback
      targetElement.style.opacity = '0.7';

      // Also add the class directly to the image
      targetElement.classList.add('seat-in-cart');

      // If this image is inside a g element, ensure parent has the class too
      const parentG = targetElement.parentElement;
      if (parentG && parentG.nodeName === 'g') {
        parentG.classList.add('seat-in-cart');
      }
    } else {
      // For other elements, try both fill and stroke
      targetElement.setAttribute('stroke', cartColor);
      targetElement.setAttribute('stroke-width', '3');
    }
  }

  formatCurrency(cents) {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: this.currency,
    }).format(cents / 100);
  }

  increment(seatId, seatingPlanId) {
    this.fireEventOnTicketSelector(
      seatId,
      'howler:seating-plan.increment',
      seatingPlanId,
    );
  }

  decrement(seatId, seatingPlanId) {
    this.fireEventOnTicketSelector(
      seatId,
      'howler:seating-plan.decrement',
      seatingPlanId,
    );
  }

  fireEventOnTicketSelector(seatId, eventName, seatingPlanId) {
    // Validate seatId is a string and doesn't contain potentially harmful patterns
    if (
      !seatId ||
      typeof seatId !== 'string' ||
      seatId.includes('__proto__') ||
      seatId.includes('constructor') ||
      seatId.includes('prototype') ||
      seatId.includes('.') ||
      seatId.includes('[') ||
      seatId.includes(']')
    ) {
      console.error('Invalid seat ID:', seatId);
      return;
    }

    // Safely access the ticket_type property using hasOwnProperty check.
    // Only read, never mutated, so no need to deep clone.
    const seatConfig = Object.prototype.hasOwnProperty.call(
      this.seatingConfig,
      seatId,
    )
      ? this.seatingConfig[seatId]
      : null;
    const ticketTypeRef = seatConfig?.ticket_type;

    if (
      typeof ticketTypeRef !== 'string' ||
      ticketTypeRef.includes('__proto__') ||
      ticketTypeRef.includes('constructor') ||
      ticketTypeRef.includes('prototype') ||
      ticketTypeRef.includes('.') ||
      ticketTypeRef.includes('[') ||
      ticketTypeRef.includes(']')
    ) {
      console.error('Invalid ticket type reference:', ticketTypeRef);
      return;
    }

    // Additional validation against other potential attack vectors
    if (ticketTypeRef && ticketTypeRef.length > 100) {
      console.error('Ticket type reference is too long:', ticketTypeRef);
      return;
    }

    let ticketTypeId = null;

    // Case 1: If the reference is a numeric ID
    if (ticketTypeRef && ticketTypeRef.toString().match(/^\d+$/)) {
      // Find ticket by ID
      const foundTicketType = Object.entries(this.ticketTypes).find(
        ([, data]) => data.id.toString() === ticketTypeRef.toString(),
      );

      if (foundTicketType) {
        ticketTypeId = foundTicketType[1].id;
      }
    }
    // Case 2: If the reference is a name
    else if (
      ticketTypeRef &&
      typeof ticketTypeRef === 'string' &&
      !['__proto__', 'constructor', 'prototype'].includes(ticketTypeRef) &&
      !ticketTypeRef.includes('.') &&
      !ticketTypeRef.includes('[') &&
      !ticketTypeRef.includes(']') &&
      Object.prototype.hasOwnProperty.call(this.ticketTypes, ticketTypeRef)
    ) {
      // Only read, never mutated - no need to deep clone
      ticketTypeId = this.ticketTypes[ticketTypeRef].id;
    }

    if (!ticketTypeId) {
      console.error(
        'Cannot find matching ticket ID for reference:',
        ticketTypeRef,
      );
      return;
    }

    // IMPORTANT: Use the original Rails.fire() with jQuery selector approach
    // This is what worked in the original codebase
    try {
      // Check if jQuery is available
      if (
        typeof $ === 'function' &&
        typeof Rails !== 'undefined' &&
        typeof Rails.fire === 'function'
      ) {
        // Try the original selector format
        const selector = `[data-ticket-type-id="${ticketTypeId}"] .ticket`;
        const elements = $(selector).get();

        if (elements && elements.length > 0) {
          Rails.fire(elements[0], eventName);
          return true;
        }

        // Try alternative selectors if the original format didn't work
        const alternativeSelectors = [
          `[data-ticket-type-id="${ticketTypeId}"]`,
          `input[name^="ticket_order[ticket_type_ids][${ticketTypeId}]"]`,
          `input[name="ticket_order[tickets][][ticket_type_id]"][value="${ticketTypeId}"]`,
        ];

        for (const altSelector of alternativeSelectors) {
          const altElements = $(altSelector).get();

          if (altElements && altElements.length > 0) {
            Rails.fire(altElements[0], eventName);
            return true;
          }
        }
      } else {
        console.error(
          'jQuery or Rails.fire not available, falling back to direct DOM manipulation',
        );
      }
    } catch (error) {
      console.error('Error using Rails.fire:', error);
    }

    // If we reached here, the Rails.fire approach failed or is not available
    // Fall back to direct DOM manipulation
    this.directSeatAction(eventName, ticketTypeId, seatId, seatingPlanId);
  }

  directSeatAction(eventName, ticketTypeId, seatId, seatingPlanId) {
    // Try using the ticket selection controller if available
    const ticketSelectionController =
      this.application.getControllerForElementAndIdentifier(
        document.querySelector('[data-controller~="ticket-selection"]'),
        'ticket-selection',
      );

    if (ticketSelectionController) {
      // Directly call the methods on the controller based on the event name
      if (eventName.includes('increment')) {
        if (typeof ticketSelectionController.incrementTicket === 'function') {
          ticketSelectionController.incrementTicket(ticketTypeId);
        }
      } else if (eventName.includes('decrement')) {
        if (typeof ticketSelectionController.decrementTicket === 'function') {
          ticketSelectionController.decrementTicket(ticketTypeId);
        }
      }
    } else {
      // Try to find and click the increment/decrement buttons directly
      const ticketBlock = document.querySelector(
        `[data-ticket-type-id="${ticketTypeId}"]`,
      );
      if (ticketBlock) {
        if (eventName.includes('increment')) {
          const incrementButton = ticketBlock.querySelector('.btn--increment');
          if (incrementButton) {
            incrementButton.click();
          }
        } else if (eventName.includes('decrement')) {
          const decrementButton = ticketBlock.querySelector('.btn--decrement');
          if (decrementButton) {
            decrementButton.click();
          }
        }
      }
    }

    // Update the hidden seating plan inputs directly
    this.updateHiddenSeatingPlanInputs(eventName, seatId, seatingPlanId);
  }

  updateHiddenSeatingPlanInputs(eventName, seatId, seatingPlanId) {
    // Check if a hidden input for the seat already exists
    const existingInput = document.querySelector(
      `input[name="ticket_order[seating_plan_seats][][seat_id]"][value="${seatId}"]`,
    );

    if (eventName.includes('increment') && !existingInput) {
      // Add a new hidden input for the seat
      const form = document.querySelector('#ticket_order_form');
      if (form) {
        const seatInput = document.createElement('input');
        seatInput.type = 'hidden';
        seatInput.name = 'ticket_order[seating_plan_seats][][seat_id]';
        seatInput.value = seatId;

        const planInput = document.createElement('input');
        planInput.type = 'hidden';
        planInput.name = 'ticket_order[seating_plan_seats][][seating_plan_id]';
        planInput.value = seatingPlanId;

        form.appendChild(seatInput);
        form.appendChild(planInput);
      }
    } else if (eventName.includes('decrement') && existingInput) {
      // Remove the hidden input for the seat
      const planInput = document.querySelector(
        `input[name="ticket_order[seating_plan_seats][][seating_plan_id]"][value="${seatingPlanId}"]`,
      );

      if (existingInput) existingInput.remove();
      if (planInput) planInput.remove();
    }
  }

  hideLinkedTickets() {
    // Get all ticket types with seats in the seating plan
    const ticketTypeIds = [];

    // Extract all ticket type IDs from the seating config
    Object.values(this.seatingConfig).forEach((config) => {
      if (config.ticket_type && config.ticket_type !== '') {
        ticketTypeIds.push(config.ticket_type);
      }
    });

    // Find and hide each ticket element on the page
    ticketTypeIds.forEach((ticketTypeId) => {
      // Look for the input with the specific name pattern
      const ticketInputs = document.querySelectorAll(
        `input[name^="ticket_order[ticket_type_ids][${ticketTypeId}]"]`,
      );

      ticketInputs.forEach((input) => {
        // Find the parent ticket element
        const ticketElement = input.closest('.ticket');
        if (ticketElement) {
          ticketElement.style.display = 'none';
        }
      });
    });
  }

  addSeatStyles() {
    const styleId = `seating-styles-${this.seatingPlanId || 'default'}`;

    // Check if styles already exist
    if (document.getElementById(styleId)) return;

    // Create a style element
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .clickable-seat {
        cursor: pointer;
      }
      /* Seat availability dimming, applied once per seat instead of walking
         every descendant individually */
      .seat-dimmed,
      .seat-dimmed rect,
      .seat-dimmed circle,
      .seat-dimmed path,
      .seat-dimmed image {
        opacity: 0.5;
      }
      .seat-active,
      .seat-active rect,
      .seat-active circle,
      .seat-active path,
      .seat-active image {
        opacity: 1;
      }
      .seat-selected {
        cursor: pointer;
      }
      .seat-in-cart {
        cursor: pointer;
      }
      /* Specific styles for image elements */
      image.clickable-seat.seat-selected {
        filter: brightness(1.1) contrast(1.1) saturate(1.2) hue-rotate(-20deg);
      }
      image.clickable-seat.seat-in-cart {
        filter: hue-rotate(42deg);
        outline: 6px solid #fff !important;
        border-radius: 1rem !important;
        outline-offset: -2px !important;
      }
      /* Highlight backgrounds for image elements */
      rect.seat-highlight-selected {
        pointer-events: none;
        box-shadow: 0 0 15px 5px rgba(255, 158, 183, 0.7); /* Pink glow */
      }
      rect.seat-highlight-cart {
        pointer-events: none;
        box-shadow: 0 0 15px 5px rgba(157, 141, 241, 0.7); /* Light purple glow */
      }
    `;

    // Append to document head
    document.head.appendChild(style);
  }

  createHighlightEffect(targetElement, color, type) {
    // Get parent SVG and element position
    const parentG = targetElement.parentElement;
    if (!parentG || parentG.nodeName !== 'g') return;

    // Remove any existing highlight
    this.removeHighlightEffect(parentG);

    // Get element size and position, accounting for transforms
    let width, height, x, y;

    if (targetElement.nodeName === 'image') {
      // For image elements, use the width and height attributes
      width = parseFloat(targetElement.getAttribute('width') || 30);
      height = parseFloat(targetElement.getAttribute('height') || 30);
      x = parseFloat(targetElement.getAttribute('x') || 0);
      y = parseFloat(targetElement.getAttribute('y') || 0);

      const transform = targetElement.getAttribute('transform');
      if (transform) {
        const translateMatch = transform.match(/translate\(([^)]+)\)/);
        if (translateMatch && translateMatch[1]) {
          const translate = translateMatch[1].split(/\s+|,/);
          if (translate.length >= 2) {
            x = parseFloat(translate[0]);
            y = parseFloat(translate[1]);
          }
        }

        const scaleMatch = transform.match(/scale\(([^)]+)\)/);
        const scale =
          scaleMatch && scaleMatch[1] ? parseFloat(scaleMatch[1]) : null;
        if (scale && !isNaN(scale)) {
          width *= scale;
          height *= scale;
        }
      }
    } else {
      // For other elements, use getBBox() if available
      try {
        const bbox = targetElement.getBBox();
        width = bbox.width;
        height = bbox.height;
        x = bbox.x;
        y = bbox.y;
      } catch (e) {
        // Fallback if getBBox() fails
        width = parseFloat(targetElement.getAttribute('width') || 30);
        height = parseFloat(targetElement.getAttribute('height') || 30);
        x = parseFloat(targetElement.getAttribute('x') || 0);
        y = parseFloat(targetElement.getAttribute('y') || 0);
      }
    }

    // Create rect element as background highlight
    const highlight = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'rect',
    );
    highlight.setAttribute('class', `seat-highlight-${type}`);
    highlight.setAttribute('x', x - 8); // Make bigger than the image
    highlight.setAttribute('y', y - 8);
    highlight.setAttribute('width', width + 16);
    highlight.setAttribute('height', height + 16);
    highlight.setAttribute('rx', '5');
    highlight.setAttribute('ry', '5');
    highlight.setAttribute('fill', color);
    highlight.setAttribute('fill-opacity', '0.5'); // More visible
    highlight.setAttribute('stroke', color);
    highlight.setAttribute('stroke-width', '3'); // Thicker border

    // Insert before the image so it appears behind it
    parentG.insertBefore(highlight, targetElement);

    // Store reference to highlight element
    parentG.dataset.highlightId = `highlight-${Date.now()}`;
    highlight.id = parentG.dataset.highlightId;
  }

  removeHighlightEffect(parentElement) {
    // Check if there's a highlight element to remove
    if (parentElement.dataset.highlightId) {
      const highlightElement = document.getElementById(
        parentElement.dataset.highlightId,
      );
      if (highlightElement) {
        highlightElement.remove();
      }
      delete parentElement.dataset.highlightId;
    }

    // Also remove any elements with the highlight class
    const highlights = parentElement.querySelectorAll(
      '.seat-highlight-selected, .seat-highlight-cart',
    );
    highlights.forEach((el) => el.remove());
  }

  storeOriginalStyling(seat, targetElement) {
    // Create an object to store original styling
    const originalStyle = {};

    // Capture fill attribute and style
    originalStyle.fill = targetElement.getAttribute('fill') || '';

    // Capture stroke attributes
    originalStyle.stroke = targetElement.getAttribute('stroke') || 'none';
    originalStyle.strokeWidth =
      targetElement.getAttribute('stroke-width') || '0';

    // Capture filter
    originalStyle.filter = targetElement.getAttribute('filter') || 'none';

    // Capture CSS properties
    originalStyle.opacity = targetElement.style.opacity || '1';
    originalStyle.backgroundColor =
      targetElement.style.backgroundColor || 'transparent';
    originalStyle.borderRadius = targetElement.style.borderRadius || '0';
    originalStyle.boxShadow = targetElement.style.boxShadow || 'none';
    originalStyle.outline = targetElement.style.outline || 'none';
    originalStyle.outlineOffset = targetElement.style.outlineOffset || '0';

    // Store class list (excluding highlighting classes)
    const classList = Array.from(targetElement.classList).filter(
      (cls) => !['seat-selected', 'seat-in-cart'].includes(cls),
    );
    originalStyle.classes = classList;

    // For image elements, also store specific image properties
    if (targetElement.nodeName === 'image') {
      // Explicitly store xlink:href if needed
      originalStyle.href =
        targetElement.getAttributeNS('http://www.w3.org/1999/xlink', 'href') ||
        '';

      // Store transform if present
      if (targetElement.hasAttribute('transform')) {
        originalStyle.transform = targetElement.getAttribute('transform');
      }
    }

    // Keep the style object in memory keyed by the seat element, instead of
    // round-tripping it through JSON in a dataset attribute on every seat
    this.originalStyles.set(seat, originalStyle);
  }

  findTargetableElement(seat) {
    // Check different element types in order of likelihood
    if (seat.nodeName === 'rect') return seat;
    if (seat.nodeName === 'image') return seat;
    if (seat.nodeName === 'path') return seat;
    if (seat.nodeName === 'circle') return seat;

    // If it's a group or container, look for children we can style
    const image = seat.querySelector('image');
    if (image) return image;

    const rect = seat.querySelector('rect');
    if (rect) return rect;

    const path = seat.querySelector('path');
    if (path) return path;

    const circle = seat.querySelector('circle');
    if (circle) return circle;

    // If no specific element found, return the seat itself
    return seat;
  }

  cleanupSeatingPlan() {
    // Remove document click handler if it exists
    if (this.boundDocumentClickHandler) {
      document.removeEventListener('click', this.boundDocumentClickHandler);
    }

    document.removeEventListener('remove-seat', this.handleSeatRemove);

    document.removeEventListener('seating-area-click', this.handleAreaClick);

    // Remove delegated seat click handler if it exists
    if (this.boundHandleSeatClick) {
      this.svgTarget.removeEventListener('click', this.boundHandleSeatClick);
      this.boundHandleSeatClick = null;
    }

    this.originalStyles?.clear();

    // Clean up ActionCable subscription if it exists
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    // Restore any modified element states
    const modifiedElements = document.querySelectorAll(
      '[data-original-z-index]',
    );
    modifiedElements.forEach((element) => {
      if (element.dataset.originalZIndex) {
        element.style.zIndex = element.dataset.originalZIndex;
        delete element.dataset.originalZIndex;
      }
    });
  }

  makeStylesUnique() {
    const svgElement = this.svgTarget.querySelector('svg');
    if (!svgElement) return;

    // Use the seating plan ID to create unique class prefixes
    const idSuffix = this.seatingPlanId ? `-${this.seatingPlanId}` : '';

    // First, find and modify the style element in the SVG
    const styleElement = svgElement.querySelector('style');
    if (styleElement && styleElement.textContent) {
      // Update all class selectors in the style with the seating plan ID suffix
      let cssText = styleElement.textContent;

      // Find all class selectors (e.g., .st0, .st1, etc.)
      const classRegex = /\.st(\d+)/g;
      const updatedCss = cssText.replace(classRegex, `.st$1${idSuffix}`);

      // Update the style element with the new CSS
      styleElement.textContent = updatedCss;
    }

    // Next, update all class attributes on SVG elements
    const elementsWithClasses = svgElement.querySelectorAll('[class]');
    elementsWithClasses.forEach((element) => {
      const classes = element.getAttribute('class').split(' ');
      const updatedClasses = classes.map((cls) => {
        // Only update style classes (st0, st1, etc.) but leave other classes intact
        if (cls.match(/^st\d+$/)) {
          return `${cls}${idSuffix}`;
        }
        return cls;
      });
      element.setAttribute('class', updatedClasses.join(' '));
    });
  }

  safelyParseHTML(html) {
    /**
     * Safely parse HTML to prevent XSS attacks.
     *
     * This function uses the DOMParser API to parse the HTML string into a Document object.
     * It thoroughly sanitizes the resulting DOM to prevent XSS attacks by removing any
     * potentially dangerous elements and attributes.
     *
     * @param {string} html - The HTML string to parse.
     * @returns {Document} The parsed HTML as a DOM element.
     */
    // First sanitize the input to remove any obviously malicious content
    if (!html || typeof html !== 'string') {
      return document.createElement('div');
    }

    // Pre-filter obviously malicious content
    const preFilteredHtml = html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/on\w+="[^"]*"/gi, '')
      .replace(/on\w+='[^']*'/gi, '')
      .replace(/on\w+=\w+/gi, '')
      .replace(/javascript:/gi, 'removed:');

    const parser = new DOMParser();
    const parsedDoc = parser.parseFromString(preFilteredHtml, 'text/html');

    // Remove dangerous elements
    const dangerousElements = parsedDoc.querySelectorAll(
      'script, object, embed, iframe, applet, meta, base, form, input, button',
    );
    dangerousElements.forEach((element) => element.remove());

    // Remove dangerous attributes from all elements
    const allElements = parsedDoc.querySelectorAll('*');
    const dangerousAttrs = [
      'onclick',
      'ondblclick',
      'onmousedown',
      'onmousemove',
      'onmouseout',
      'onmouseover',
      'onmouseup',
      'onload',
      'onunload',
      'onchange',
      'onsubmit',
      'onreset',
      'onselect',
      'onblur',
      'onfocus',
      'onkeydown',
      'onkeypress',
      'onkeyup',
      'onerror',
      'src',
      'href',
      'xlink:href',
      'data',
    ];

    allElements.forEach((element) => {
      dangerousAttrs.forEach((attr) => {
        if (element.hasAttribute(attr)) {
          const attrValue = element.getAttribute(attr);
          if (attr === 'src' || attr === 'href' || attr === 'xlink:href') {
            // Allow safe URLs but remove javascript: and data: URLs
            if (
              attrValue.toLowerCase().startsWith('javascript:') ||
              attrValue.toLowerCase().startsWith('data:')
            ) {
              element.removeAttribute(attr);
            }
          } else {
            element.removeAttribute(attr);
          }
        }
      });
    });

    return parsedDoc.body;
  }

  sanitizeHTML(html) {
    // This function completely strips HTML tags from the input string.
    // It does not preserve any HTML, and is intended for use when displaying
    // user-provided text that should not contain HTML.
    const sanitizedDiv = document.createElement('div');
    sanitizedDiv.textContent = html;
    return sanitizedDiv.textContent;
  }

  safelySetContent(targetElement, sourceElement) {
    // Clear any existing content in the target
    while (targetElement.firstChild) {
      targetElement.removeChild(targetElement.firstChild);
    }

    // Clone all nodes from the source and append them to the target
    const fragment = document.createDocumentFragment();
    Array.from(sourceElement.childNodes).forEach((node) => {
      fragment.appendChild(node.cloneNode(true));
    });

    // Append all nodes at once for better performance
    targetElement.appendChild(fragment);
  }

  refresh = async (e) => {
    e?.preventDefault();

    const seatingPlanElement = this.svgTarget.closest(
      '[data-seating-plan-config]',
    );
    if (!seatingPlanElement)
      return console.error('No seating plan element found for refresh');

    try {
      await Promise.all([
        this.fetchConfig(seatingPlanElement),
        this.fetchTicketTypes(seatingPlanElement),
      ]);

      const seatsToRemove = [];

      for (const [, seatData] of this.selectedSeats) {
        const config = this.seatingConfig?.[seatData.seatId];
        const stillSelectable =
          config &&
          config.ticket_type &&
          (config.can_select === true ||
            config.can_select === 'true' ||
            config.can_select == 1);
        if (!stillSelectable) {
          seatsToRemove.push(seatData);
        }
      }

      for (const seatData of seatsToRemove) {
        this.selectedSeats.delete(seatData.uniqueId);
        this.decrement(seatData.seatId, seatData.seatingPlanId);

        const seatEl =
          seatData.element ||
          this.svgTarget.querySelector(
            `[data-id="${CSS.escape(seatData.seatId)}"]`,
          );
        if (seatEl) {
          this.resetSeatColor(seatEl);
        }

        if (this.activeSeat?.uniqueId === seatData.uniqueId) {
          this.activeSeat = null;
        }

        this.hideSeatInfo(seatData.seatId);
      }

      if (seatsToRemove.length) this.persistSelectedSeatIds();

      this.setupClickHandlers();

      if (this.selectedSeats.size === 0) {
        this.hideNotification();
      } else {
        if (seatData && addDirectToCart) return;

        this.showNotification(
          `You have <span>${this.selectedSeats.size}</span> ${
            this.selectedSeats.size > 1 ? 'Tables' : 'Table'
          } in your cart.`,
        );
      }

      this.hideLinkedTickets();
      this.hidePopup();
    } catch (error) {
      console.error('Error refreshing seating plan:', error);
    }
  };

  interact = (e) => {
    e.preventDefault();

    const interaction = e.target.dataset.interaction;

    const event = new CustomEvent('seating-plan-interaction', {
      bubbles: true,
      detail: { interaction: interaction },
    });
    this.element.dispatchEvent(event);
  };

  handleAreaClick = (e) => {
    e.preventDefault();

    const area = e?.detail?.area;
    if (!area) return;

    let clickedArea;

    for (const ticket of this.looseTickets) {
      const ecommerceData = JSON.parse(ticket.getAttribute('ecommerce-data'));

      if (
        ecommerceData.items[0].item_name
          .toLowerCase()
          .includes(area.toLowerCase())
      ) {
        clickedArea = ticket;
        break;
      }
    }

    clickedArea.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };
}
